import type { CreateSnapshotBody, Snapshot, SnapshotDetail, ProjectSnapshot } from '../../types/snapshot'

async function json<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.error || 'Erreur snapshot')
  return body.data as T
}

/** Crée un snapshot complet (fige pages + données) pour un rapport. */
export async function createSnapshot(
  clientId: number,
  reportId: number,
  body: CreateSnapshotBody
): Promise<Snapshot> {
  const res = await fetch(`/api/reporting/clients/${clientId}/reports/${reportId}/snapshots`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  return json<Snapshot>(res)
}

/** Liste légère des snapshots d'un rapport. */
export async function listSnapshots(clientId: number, reportId: number): Promise<Snapshot[]> {
  const res = await fetch(`/api/reporting/clients/${clientId}/reports/${reportId}/snapshots`, {
    credentials: 'include',
  })
  return json<Snapshot[]>(res)
}

/** Liste de TOUS les snapshots du projet (tous rapports confondus), avec le nom du rapport. */
export async function listProjectSnapshots(clientId: number): Promise<ProjectSnapshot[]> {
  const res = await fetch(`/api/reporting/clients/${clientId}/snapshots`, { credentials: 'include' })
  return json<ProjectSnapshot[]>(res)
}

/** Détail complet d'un snapshot (pages figées + données figées). */
export async function getSnapshot(id: number): Promise<SnapshotDetail> {
  const res = await fetch(`/api/snapshots/${id}`, { credentials: 'include' })
  return json<SnapshotDetail>(res)
}

/**
 * Détail public d'un snapshot via lien de partage (hors auth).
 * L'URL partagée est /r/:slug/:token (page SPA), mais le fetch de données passe par /api/
 * (proxifié vers le backend) — sinon nginx/Vite renverrait le HTML du SPA au lieu du JSON.
 */
export async function getPublicSnapshot(slug: string, token: string): Promise<SnapshotDetail> {
  const res = await fetch(`/api/public-snapshots/${slug}/${token}`)
  return json<SnapshotDetail>(res)
}

/**
 * Détail d'un snapshot pour rendu PDF serveur (Playwright) via token dédié.
 * Sans credentials : Playwright n'a pas de cookie de session.
 */
export async function getSnapshotForRender(id: number, pdfToken: string): Promise<SnapshotDetail> {
  const res = await fetch(`/api/snapshots/${id}/render?pdf_token=${encodeURIComponent(pdfToken)}`)
  return json<SnapshotDetail>(res)
}

/** Déclenche le téléchargement du PDF d'un snapshot (authentifié). */
export function downloadSnapshotPdf(id: number): void {
  window.location.href = `/api/snapshots/${id}/pdf`
}

/** Déclenche le téléchargement du PDF public d'un snapshot (via lien de partage). */
export function downloadPublicSnapshotPdf(slug: string, token: string): void {
  window.location.href = `/api/public-snapshots/${slug}/${token}/pdf`
}

/** Supprime (soft delete) un snapshot. */
export async function deleteSnapshot(id: number): Promise<void> {
  const res = await fetch(`/api/snapshots/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  await json<null>(res)
}
